import { Box, CircularProgress, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Model } from 'survey-core';
import 'survey-core/survey-core.css';
import { DefaultLightPanelless } from 'survey-core/themes';
import { Survey } from 'survey-react-ui';
import { getSurveyJSJson } from '../api';
import { useETI } from '../hooks';

export function ETIPreview() {
  const { taskId } = useParams();
  const { data, isLoading, error } = useQuery({
    queryKey: ['eti', 'surveyjs', taskId],
    queryFn: () => getSurveyJSJson(Number(taskId)),
  });

  const { eti, isLoading: isETILoading } = useETI(Number(taskId));

  const survey = useMemo(() => {
    const model = new Model(data);
    model.applyTheme(DefaultLightPanelless);
    model.showCompleteButton = false;

    model.getAllQuestions().forEach(question => {
      if (question.isRequired) question.isRequired = false;
    });

    // preview for file upload question
    model.onUploadFiles.add((_, options) => {
      const maxFileCount = 3;

      const currentFileCount = options.question.value?.length || 0;
      if (currentFileCount + options.files.length > maxFileCount) {
        options.callback(
          null,
          'You can only upload a maximum of ' + maxFileCount + ' files.',
        );
        return;
      }
      const uploaded = options.files.map(file => {
        const objectUrl = URL.createObjectURL(file);
        return {
          file,
          content: objectUrl,
          contentMode: 'link',
        };
      });
      options.callback(uploaded);
    });

    model.onClearFiles.add((_, options) => {
      options.callback('success');
    });

    return model;
  }, [data]);

  if (isLoading || isETILoading)
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: '90vh',
          flexDirection: 'column',
        }}
      >
        <CircularProgress color="info" />
        <Typography variant="h6" sx={{ mt: 4 }}>
          Loading Preview...
        </Typography>
      </Box>
    );

  if (error)
    return (
      <Typography color="error" align="center">
        Failed to load survey preview.
      </Typography>
    );

  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        alignItems: 'center',
        maxWidth: '1280px',
        margin: '0 auto',
      }}
    >
      <Box
        sx={{
          alignSelf: 'flex-start',
        }}
      >
        <Typography variant="h1" gutterBottom>
          ETI Questions Preview
        </Typography>
        <Typography variant="body2" gutterBottom>
          {`Policy/Task ID: ${eti?.policyId}/${eti?.taskId}`}
        </Typography>
      </Box>

      <Box sx={{ width: '100%' }}>
        <Survey model={survey} />
      </Box>
    </Box>
  );
}
